// NotificationUtils.js - Constants, types, and helper functions for the notification system

import { FaCar, FaEnvelope, FaBox, FaCalendarAlt, FaUsers, FaStar, FaExclamationTriangle } from 'react-icons/fa';

// Notification categories
export const NOTIFICATION_CATEGORIES = {
  MESSAGES: 'messages',
  RIDES: 'rides', 
  PACKAGES: 'packages',
  EVENTS: 'events',
  GROUPS: 'groups',
  GENERAL: 'general'
};

// Notification types
export const NOTIFICATION_TYPES = {
  // Messages
  NEW_MESSAGE: 'new_message',
  NEW_CONVERSATION: 'new_conversation',
  
  // Rides
  RIDE_REQUEST: 'ride_request',
  RIDE_ACCEPTED: 'ride_accepted',
  RIDE_CANCELLED: 'ride_cancelled',
  RIDE_COMPLETED: 'ride_completed',
  
  // Packages
  PACKAGE_REQUEST: 'package_request',
  PACKAGE_ACCEPTED: 'package_accepted',
  PACKAGE_DELIVERED: 'package_delivered',
  PACKAGE_DECLINED: 'package_declined',
  
  // Events (future)
  EVENT_INVITATION: 'event_invitation',
  EVENT_REMINDER: 'event_reminder',
  EVENT_CANCELLED: 'event_cancelled',
  
  // Groups (future)
  GROUP_INVITATION: 'group_invitation',
  NEW_GROUP_RIDE: 'new_group_ride',
  GROUP_DISCUSSION: 'group_discussion',
  
  // General
  NEW_REVIEW: 'new_review',
  SYSTEM_UPDATE: 'system_update'
};

// Notification priorities
export const NOTIFICATION_PRIORITIES = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent'
};

/**
 * Get icon component for notification category
 * @param {string} category - Notification category
 * @returns {React.Component} Icon component
 */
export const getCategoryIcon = (category) => {
  switch (category) {
    case NOTIFICATION_CATEGORIES.MESSAGES:
      return FaEnvelope;
    case NOTIFICATION_CATEGORIES.RIDES:
      return FaCar;
    case NOTIFICATION_CATEGORIES.PACKAGES:
      return FaBox;
    case NOTIFICATION_CATEGORIES.EVENTS:
      return FaCalendarAlt;
    case NOTIFICATION_CATEGORIES.GROUPS:
      return FaUsers;
    case NOTIFICATION_CATEGORIES.GENERAL:
      return FaStar;
    default:
      return FaEnvelope;
  }
};

/**
 * Get icon component for specific notification type
 * @param {string} type - Notification type
 * @returns {React.Component} Icon component
 */
export const getTypeIcon = (type) => {
  switch (type) {
    case NOTIFICATION_TYPES.NEW_MESSAGE:
    case NOTIFICATION_TYPES.NEW_CONVERSATION:
      return FaEnvelope;
      
    case NOTIFICATION_TYPES.RIDE_REQUEST:
    case NOTIFICATION_TYPES.RIDE_ACCEPTED:
    case NOTIFICATION_TYPES.RIDE_COMPLETED:
      return FaCar;
      
    case NOTIFICATION_TYPES.RIDE_CANCELLED:
      return FaExclamationTriangle;
      
    case NOTIFICATION_TYPES.PACKAGE_REQUEST:
    case NOTIFICATION_TYPES.PACKAGE_ACCEPTED:
    case NOTIFICATION_TYPES.PACKAGE_DELIVERED:
    case NOTIFICATION_TYPES.PACKAGE_DECLINED:
      return FaBox;
      
    case NOTIFICATION_TYPES.NEW_REVIEW:
      return FaStar;
      
    default:
      return FaEnvelope;
  }
};

/**
 * Get CSS class for notification priority
 * @param {string} priority - Notification priority
 * @returns {string} CSS class name
 */
export const getPriorityClass = (priority) => {
  switch (priority) {
    case NOTIFICATION_PRIORITIES.LOW:
      return 'priority-low';
    case NOTIFICATION_PRIORITIES.HIGH:
      return 'priority-high';
    case NOTIFICATION_PRIORITIES.URGENT:
      return 'priority-urgent';
    default:
      return 'priority-normal';
  }
};

/**
 * Format relative time for notifications
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted relative time
 */
export const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    return 'just now';
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }
  
  return date.toLocaleDateString();
};

/**
 * Generate action URL based on notification type and related data
 * @param {string} type - Notification type
 * @param {string} relatedId - Related object ID
 * @param {Object} data - Additional notification data
 * @returns {string} Action URL
 * @deprecated This function is deprecated. Use the switch statement in NotificationItem.js instead.
 */
export const generateActionUrl = (type, relatedId, data = {}) => {
  // This function is kept for backward compatibility but is no longer used
  // The actual navigation logic is now handled directly in NotificationItem.js
  console.warn('generateActionUrl is deprecated. Navigation is handled in NotificationItem.js');
  return null;
};

/**
 * Get display color for notification category
 * @param {string} category - Notification category
 * @returns {string} CSS color variable
 */
export const getCategoryColor = (category) => {
  switch (category) {
    case NOTIFICATION_CATEGORIES.MESSAGES:
      return 'var(--accent-color2)'; // Blue
    case NOTIFICATION_CATEGORIES.RIDES:
      return 'var(--accent-color)'; // Green
    case NOTIFICATION_CATEGORIES.PACKAGES:
      return '#ff7c41'; // Orange
    case NOTIFICATION_CATEGORIES.EVENTS:
      return '#9c27b0'; // Purple
    case NOTIFICATION_CATEGORIES.GROUPS:
      return '#795548'; // Brown
    case NOTIFICATION_CATEGORIES.GENERAL:
      return 'var(--main-color)'; // Default
    default:
      return 'var(--main-color)';
  }
};

/**
 * Check if notification requires user action
 * @param {string} type - Notification type
 * @returns {boolean} Whether notification requires action
 */
export const requiresAction = (type) => {
  // Only invitations require action buttons, ride/package requests should just open the modal
  const actionTypes = [
    NOTIFICATION_TYPES.EVENT_INVITATION,
    NOTIFICATION_TYPES.GROUP_INVITATION
  ];
  return actionTypes.includes(type);
};

/**
 * Get action button labels for notification type
 * @param {string} type - Notification type
 * @returns {Object} Object with accept and decline button labels
 */
export const getActionLabels = (type) => {
  switch (type) {
    case NOTIFICATION_TYPES.RIDE_REQUEST:
      return { accept: 'Accept', decline: 'Decline' };
    case NOTIFICATION_TYPES.PACKAGE_REQUEST:
      return { accept: 'Accept', decline: 'Decline' };
    case NOTIFICATION_TYPES.EVENT_INVITATION:
      return { accept: 'Join', decline: 'Pass' };
    case NOTIFICATION_TYPES.GROUP_INVITATION:
      return { accept: 'Join', decline: 'Decline' };
    default:
      return { accept: 'Accept', decline: 'Decline' };
  }
};

/**
 * Determine if notification should show as toast
 * @param {string} type - Notification type
 * @param {string} priority - Notification priority
 * @returns {boolean} Whether to show toast
 */
export const shouldShowToast = (type, priority = NOTIFICATION_PRIORITIES.NORMAL) => {
  // Always show toast for urgent notifications
  if (priority === NOTIFICATION_PRIORITIES.URGENT) {
    return true;
  }
  
  // Show toast for important notification types
  const toastTypes = [
    NOTIFICATION_TYPES.NEW_MESSAGE,
    NOTIFICATION_TYPES.RIDE_REQUEST,
    NOTIFICATION_TYPES.RIDE_ACCEPTED,
    NOTIFICATION_TYPES.PACKAGE_REQUEST,
    NOTIFICATION_TYPES.PACKAGE_ACCEPTED,
    NOTIFICATION_TYPES.RIDE_CANCELLED
  ];
  
  return toastTypes.includes(type);
};

/**
 * Create toast notification object
 * @param {Object} notification - Notification object
 * @returns {Object} Toast notification object
 */
export const createToastFromNotification = (notification) => {
  const { type, category, message, priority, actor_name } = notification;
  
  // Determine toast type based on notification
  let toastType = 'info';
  if (type === NOTIFICATION_TYPES.RIDE_CANCELLED || type === NOTIFICATION_TYPES.PACKAGE_DECLINED) {
    toastType = 'warning';
  } else if (type === NOTIFICATION_TYPES.RIDE_ACCEPTED || type === NOTIFICATION_TYPES.PACKAGE_ACCEPTED) {
    toastType = 'success';
  } else if (priority === NOTIFICATION_PRIORITIES.URGENT) {
    toastType = 'error';
  }
  
  return {
    type: toastType,
    title: getCategoryDisplayName(category),
    description: message,
    duration: priority === NOTIFICATION_PRIORITIES.URGENT ? 10000 : 5000
  };
};

/**
 * Get display name for category
 * @param {string} category - Notification category
 * @returns {string} Display name
 */
export const getCategoryDisplayName = (category) => {
  switch (category) {
    case NOTIFICATION_CATEGORIES.MESSAGES:
      return 'New Message';
    case NOTIFICATION_CATEGORIES.RIDES:
      return 'Ride Update';
    case NOTIFICATION_CATEGORIES.PACKAGES:
      return 'Package Update';
    case NOTIFICATION_CATEGORIES.EVENTS:
      return 'Event Update';
    case NOTIFICATION_CATEGORIES.GROUPS:
      return 'Group Update';
    case NOTIFICATION_CATEGORIES.GENERAL:
      return 'Notification';
    default:
      return 'Notification';
  }
};

/**
 * Truncate notification message for display
 * @param {string} message - Full message
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated message
 */
export const truncateMessage = (message, maxLength = 80) => {
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength).trim() + '...';
};
